import React, { useEffect, useRef, useState } from 'react';
import { BiSolidErrorCircle } from 'react-icons/bi';
import { IoCloseOutline } from 'react-icons/io5';

import { transparentize } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import Button, { buttonTooltip } from 'components/button/Button';

import { getArrowPositionStyle } from 'styles/components/getPositionStyle';

/**
 * @desc 정보 버튼 컴포넌트
 * @param {string} text - 버튼에 표시할 텍스트
 * @param {ReactNode} icon - 버튼에 사용할 아이콘
 * @param {ReactNode} beforeIcon - 버튼 텍스트 앞에 표시할 아이콘
 * @param {string} info - 툴팁에 표시할 정보
 * @param {string} arrow - 툴팁의 화살표 방향 (기본값: "top")
 * @param {boolean} visible - 툴팁의 초기 가시성 상태 (기본값: false)
 * @param {function} onClick - 버튼 클릭 시 실행할 함수
 * @param {function} onClose - 툴팁 닫기 버튼 클릭 시 실행할 함수
 * @returns {JSX.Element} 정보 버튼 컴포넌트
 */
const InfoButton = ({
  children,
  icon,
  beforeIcon,
  disabled = false,
  info,
  arrow = 'right',
  className,
  visible = false,
  onClick,
  isClose = true,
  onClose,
  isOutside = true,
  isIcon = false,
  shape,
  variant,
  size,
  disableClickToggle = false,
}) => {
  const [isInfoVisible, setIsInfoVisible] = useState(visible);
  const infoboxRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    isOutside && document.addEventListener('mousedown', handleClickOutside);
    return () => {
      isOutside &&
        document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOutside]);

  useEffect(() => {
    setIsInfoVisible(visible);
  }, [visible]);

  const handleClick = () => {
    if (!disableClickToggle) {
      !isInfoVisible && setIsInfoVisible((prev) => !prev);
    }
    onClick && onClick();
  };
  const handleClose = (e) => {
    e.stopPropagation();
    isInfoVisible && setTimeout(() => setIsInfoVisible(false), 0);
    onClose && onClose();
  };

  /**
   * 외부 클릭 시 드롭다운을 닫기 위한 핸들러
   * @param {Event} event - 클릭 이벤트
   */
  const handleClickOutside = (event) => {
    if (infoboxRef.current && !infoboxRef.current.contains(event.target)) {
      setIsInfoVisible(false);
    }
  };

  return (
    <InfoButtonStyle
      type="button"
      icon={
        isIcon || icon ? (
          <BiSolidErrorCircle
            style={{
              marginLeft: '4px',
              fontSize: '17px',
              fill: transparentize(0.7, theme.color.gray600),
            }}
          />
        ) : (
          icon
        )
      }
      beforeIcon={beforeIcon}
      onClick={handleClick}
      disabled={disabled}
      className={className}
      shape={shape}
      variant={variant}
      size={size}
      disableClickToggle={disableClickToggle}
    >
      {children}
      {/* 툴팁 표시 */}
      {isInfoVisible && (
        <InfoBox arrow={arrow} className="info_button_box" ref={infoboxRef}>
          <InfoContent className="info_content" isclose={isClose.toString()}>
            {info}
            {isClose && (
              <CloseButton className="close_btn" onClick={handleClose}>
                <IoCloseOutline />
              </CloseButton>
            )}
          </InfoContent>
        </InfoBox>
      )}
    </InfoButtonStyle>
  );
};

export default InfoButton;

const infoButtonClassName = {
  app_setting_info_btn: css`
    .info_button_box {
      .info_content {
        width: -webkit-fill-available;
      }
    }
  `,
};
const InfoButtonStyle = styled(Button)`
  position: relative;
  svg {
    width: 20px;
  }
  ${({ className }) => infoButtonClassName[className] || ''}
`;

const InfoBox = styled.div`
  position: absolute;
  white-space: nowrap;
  z-index: 10;
  border-radius: 4px;
  ${buttonTooltip}
  ${({ arrow }) => getArrowPositionStyle(arrow) || ''}
`;

export const InfoContent = styled.div`
  position: relative;
  padding: ${({ isclose }) =>
    isclose === 'true' ? '10px 24px 10px 10px' : '10px'};
  text-align: left;
  font-size: 12px;
  line-height: 1.4;
  white-space: pre-wrap;
  min-width: ${({ isclose }) => (isclose === 'true' ? '180px' : '110px')};
`;

const CloseButton = styled.div`
  position: absolute;
  top: 12px;
  right: 2px;
  height: auto;
  transform: translateY(-50%);

  svg {
    width: auto;
    font-size: 13px;
  }
`;
